import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../services/supabaseClient'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [schoolsLoading, setSchoolsLoading] = useState(true)
  const [allowedSchools, setAllowedSchools] = useState([])
  const [activeSchoolId, setActiveSchoolIdState] = useState(() => {
    const v = typeof window !== 'undefined' ? window.localStorage.getItem('gateActiveSchoolId') : null
    return v ? Number(v) : null
  })

  useEffect(() => {
    let mounted = true
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return
      setSession(data.session || null)
      setUser(data.session?.user || null)
      setAuthReady(true)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (!mounted) return
      setSession(sess)
      setUser(sess?.user || null)
    })
    return () => {
      mounted = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const setActiveSchoolId = useCallback((id) => {
    setActiveSchoolIdState(id)
    if (typeof window !== 'undefined') {
      if (id) window.localStorage.setItem('gateActiveSchoolId', String(id))
      else window.localStorage.removeItem('gateActiveSchoolId')
    }
  }, [])

  const fetchAllowedSchools = useCallback(async () => {
    if (!user) { setAllowedSchools([]); return }

    // Prefer manager view for manager accounts; fallback to unified user view for teacher/staff.
    let rows = []
    const { data: mas, error: masErr } = await supabase
      .from('manager_allowed_schools')
      .select('school_id, school_name')

    if (masErr) {
      console.error('Failed to load manager_allowed_schools', masErr)
    } else {
      rows = (mas || []).map(r => ({
        school_id: Number(r.school_id),
        school_name: r.school_name,
        role: 'manager',
      }))
    }

    if (!rows.length) {
      const { data: uas, error: uasErr } = await supabase
        .from('user_allowed_schools')
        .select('school_id, school_name, role')

      if (uasErr) {
        console.error('Failed to load user_allowed_schools', uasErr)
        setAllowedSchools([])
        return
      }

      rows = (uas || []).map(r => ({
        school_id: Number(r.school_id),
        school_name: r.school_name,
        role: r.role,
      }))
    }

    setAllowedSchools(rows)
    setActiveSchoolId(prev => {
      if (prev) return prev
      if (rows.length === 1) return Number(rows[0].school_id)
      return null
    })
  }, [user, setActiveSchoolId])

  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!authReady) return
      setSchoolsLoading(true)
      try {
        await fetchAllowedSchools()
      } finally {
        if (mounted) setSchoolsLoading(false)
      }
    })()
    return () => { mounted = false }
  }, [authReady, fetchAllowedSchools])

  const loading = !authReady || schoolsLoading

  const value = useMemo(() => ({
    session,
    user,
    loading,
    allowedSchools,
    activeSchoolId,
    setActiveSchoolId,
    signOut: async () => {
      setActiveSchoolId(null)
      await supabase.auth.signOut()
    },
  }), [session, user, loading, allowedSchools, activeSchoolId, setActiveSchoolId])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
